import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { DashboardHeader } from "@/components/dashboard/DashboardHeader";
import { StatCards } from "@/components/dashboard/StatCards";
import { ChartSection } from "@/components/dashboard/ChartSection";
import { TrafficAnalytics } from "@/components/analytics/TrafficAnalytics";
import { useCurrency } from "@/hooks/use-currency";
import { useRealtimeSubscription } from "@/hooks/use-realtime";
import { format, startOfMonth, endOfMonth, eachMonthOfInterval, subMonths } from "date-fns";
const Dashboard = () => {
  const {
    formatAmount,
    convertAmount,
    currency
  } = useCurrency();

  // Enable realtime updates
  useRealtimeSubscription("subscriptions", "dashboard-subscriptions");
  useRealtimeSubscription("invoices", "dashboard-invoices");
  useRealtimeSubscription("projects", "dashboard-projects");
  useRealtimeSubscription("customers", "dashboard-clients");
  useRealtimeSubscription("profiles", "dashboard-signup-clients");
  useRealtimeSubscription("service_requests", "dashboard-service-request-clients");
  useRealtimeSubscription("business_expenses", "dashboard-expenses");
  useRealtimeSubscription("service_requests", "dashboard-service-requests");

  // Fetch all data
  const {
    data: subscriptions,
    isLoading: loadingSubscriptions
  } = useQuery({
    queryKey: ["dashboard-subscriptions"],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from("subscriptions").select("*");
      if (error) throw error;
      return data;
    }
  });
  
  const {
    data: invoices,
    isLoading: loadingInvoices
  } = useQuery({
    queryKey: ["dashboard-invoices"],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from("invoices").select("*");
      if (error) throw error;
      return data;
    }
  });
  
  const {
    data: projects,
    isLoading: loadingProjects
  } = useQuery({
    queryKey: ["dashboard-projects"],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from("projects").select("*");
      if (error) throw error;
      return data;
    }
  });

  // Fetch all client sources (manual, signups, service requests)
  const {
    data: manualClients
  } = useQuery({
    queryKey: ["dashboard-clients"],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from("customers").select("*");
      if (error) throw error;
      return data;
    }
  });
  const {
    data: signupClients
  } = useQuery({
    queryKey: ["dashboard-signup-clients"],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from("profiles").select("*").eq("user_type", "client");
      if (error) throw error;
      return data;
    }
  });
  const {
    data: serviceRequestClientsData
  } = useQuery({
    queryKey: ["dashboard-service-request-clients"],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from("service_requests").select("*");
      if (error) throw error;
      // Get unique clients by email
      const uniqueClients = new Map();
      data?.forEach(request => {
        if (!uniqueClients.has(request.email)) {
          uniqueClients.set(request.email, request);
        }
      });
      return Array.from(uniqueClients.values());
    }
  });

  // Combine all client sources
  const clients = useMemo(() => {
    const clientMap = new Map();
    manualClients?.forEach(client => {
      if (client.email) {
        clientMap.set(client.email.toLowerCase(), client);
      } else {
        clientMap.set(`manual-${client.id}`, client);
      }
    });
    signupClients?.forEach(client => {
      if (client.email && !clientMap.has(client.email.toLowerCase())) {
        clientMap.set(client.email.toLowerCase(), client);
      }
    });
    serviceRequestClientsData?.forEach(client => {
      if (client.email && !clientMap.has(client.email.toLowerCase())) {
        clientMap.set(client.email.toLowerCase(), client);
      }
    });
    return Array.from(clientMap.values());
  }, [manualClients, signupClients, serviceRequestClientsData]);
  const loadingClients = !manualClients && !signupClients && !serviceRequestClientsData;
  const {
    data: expenses,
    isLoading: loadingExpenses
  } = useQuery({
    queryKey: ["dashboard-expenses"],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from("business_expenses").select("*");
      if (error) throw error;
      return data;
    }
  });
  const {
    data: serviceRequests,
    isLoading: loadingRequests
  } = useQuery({
    queryKey: ["dashboard-service-requests"],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from("service_requests").select("*");
      if (error) throw error;
      return data;
    }
  });
  const isLoading = loadingSubscriptions || loadingInvoices || loadingProjects || loadingClients || loadingExpenses || loadingRequests;

  // Calculate metrics - convert all amounts to display currency (default to USD if currency not set)
  // Include both subscriptions and invoices in revenue (exclude draft/cancelled invoices and invoice-type subscriptions to avoid double counting)
  const subscriptionRevenue = subscriptions
    ?.filter((sub) => sub.payment_type !== 'invoice')
    .reduce((acc, sub) => {
      const sourceCurrency = sub.currency as any || "USD";
      const converted = convertAmount(Number(sub.price), sourceCurrency, currency);
      return acc + converted;
    }, 0) || 0;
  
  const invoiceRevenue = invoices?.filter(inv => inv.status !== 'draft' && inv.status !== 'cancelled')
    .reduce((acc, inv) => {
      const sourceCurrency = inv.currency as any || "USD";
      const converted = convertAmount(Number(inv.total_amount), sourceCurrency, currency);
      return acc + converted;
    }, 0) || 0;
  
  const totalRevenue = subscriptionRevenue + invoiceRevenue;
  const totalExpenses = expenses?.reduce((acc, exp) => {
    const sourceCurrency = exp.currency as any || "USD";
    const converted = convertAmount(Number(exp.amount), sourceCurrency, currency);
    return acc + converted;
  }, 0) || 0;
  const netProfit = totalRevenue - totalExpenses;
  const activeSubscriptions = subscriptions?.filter(sub => sub.payment_status === 'completed' && sub.payment_type !== 'invoice')?.length || 0;
  const pendingSubscriptions = subscriptions?.filter(sub => sub.payment_status === 'pending')?.length || 0;
  const activeProjects = projects?.filter(p => p.status === 'active')?.length || 0;
  const totalClients = clients?.length || 0;
  const pendingRequests = serviceRequests?.filter(r => r.status === 'pending')?.length || 0;

  // Prepare monthly revenue data (last 6 months)
  const last6Months = eachMonthOfInterval({
    start: subMonths(new Date(), 5),
    end: new Date()
  });
  const monthlyRevenueData = last6Months.map(month => {
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    
    // Revenue from subscriptions (exclude invoice-type to avoid double counting)
    const monthSubscriptionRevenue = subscriptions
      ?.filter(sub => {
        const subDate = new Date(sub.created_at || "");
        return sub.payment_type !== 'invoice' && subDate >= monthStart && subDate <= monthEnd;
      })
      .reduce((sum, sub) => {
        const sourceCurrency = sub.currency as any || "USD";
        const converted = convertAmount(Number(sub.price), sourceCurrency, currency);
        return sum + converted;
      }, 0) || 0;
    
    // Revenue from invoices (exclude draft/cancelled)
    const monthInvoiceRevenue = invoices?.filter(inv => {
      const invDate = new Date(inv.created_at || "");
      return (inv.status !== 'draft' && inv.status !== 'cancelled') && invDate >= monthStart && invDate <= monthEnd;
    }).reduce((sum, inv) => {
      const sourceCurrency = inv.currency as any || "USD";
      const converted = convertAmount(Number(inv.total_amount), sourceCurrency, currency);
      return sum + converted;
    }, 0) || 0;
    
    const monthRevenue = monthSubscriptionRevenue + monthInvoiceRevenue;
    const monthExpenses = expenses?.filter(exp => {
      const expDate = new Date(exp.created_at);
      return expDate >= monthStart && expDate <= monthEnd;
    }).reduce((sum, exp) => {
      const sourceCurrency = exp.currency as any || "USD";
      const converted = convertAmount(Number(exp.amount), sourceCurrency, currency);
      return sum + converted;
    }, 0) || 0;
    return {
      name: format(month, "MMM"),
      revenue: monthRevenue,
      expenses: monthExpenses,
      profit: monthRevenue - monthExpenses
    };
  });

  // Prepare weekly activity data (last 7 days)
  const last7Days = Array.from({
    length: 7
  }, (_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (6 - i));
    return date;
  });
  const weeklyActivityData = last7Days.map(date => {
    const dayStart = new Date(date.setHours(0, 0, 0, 0));
    const dayEnd = new Date(date.setHours(23, 59, 59, 999));
    const dayClients = clients?.filter(client => {
      const clientDate = new Date(client.created_at);
      return clientDate >= dayStart && clientDate <= dayEnd;
    }).length || 0;
    const dayProjects = projects?.filter(project => {
      const projectDate = new Date(project.created_at);
      return projectDate >= dayStart && projectDate <= dayEnd;
    }).length || 0;
    const dayRequests = serviceRequests?.filter(request => {
      const requestDate = new Date(request.created_at);
      return requestDate >= dayStart && requestDate <= dayEnd;
    }).length || 0;
    return {
      name: format(date, "EEE"),
      active: dayClients + dayProjects + dayRequests
    };
  });
  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-muted-foreground">Loading dashboard data...</div>
      </div>;
  }
  return <div className="animate-fade-in space-y-8">
      <DashboardHeader />
      
      <div className="space-y-8">
        <StatCards totalRevenue={totalRevenue} activeSubscriptions={activeSubscriptions} pendingSubscriptions={pendingSubscriptions} netProfit={netProfit} activeProjects={activeProjects} totalClients={totalClients} pendingRequests={pendingRequests} />
        
        <ChartSection revenueData={monthlyRevenueData} userActivityData={weeklyActivityData} />
      </div>

      <div className="mt-8">
        
        <TrafficAnalytics />
      </div>
    </div>;
};
export default Dashboard;